"use client";

import { useState } from "react";

export type ProductType =
  | "unlimitedRx"
  | "pediatricSupport"
  | "pediatricSupportSingle"
  | "pediatricSupportFamily"
  // Owlet front-door experiment (PE-1471): dedicated no-trial products, kept
  // separate from the shared keys above so the dark experiment never disturbs
  // the live paywalls.
  | "owletOnCall"
  | "owletBase"
  | "owletPremium";
export type BillingPeriod = "monthly" | "yearly";

interface UsePaywallStateReturn<T extends ProductType> {
  selectedProduct: T;
  billingPeriod: BillingPeriod;
  setSelectedProduct: (product: T) => void;
  setBillingPeriod: (period: BillingPeriod) => void;
}

/**
 * Hook to manage paywall state including product selection and billing period.
 * Pricing is fetched from Stripe via usePaywallPricing hook.
 *
 * Generic over `T extends ProductType` so callers (e.g. the Kinsa two-product
 * paywall) can constrain `selectedProduct` to the subset of products they
 * actually render, instead of the union of every product in the codebase.
 */
export function usePaywallState<T extends ProductType = ProductType>(
  initialProduct: T = "unlimitedRx" as T,
  initialBillingPeriod: BillingPeriod = "monthly"
): UsePaywallStateReturn<T> {
  const [selectedProduct, setSelectedProduct] = useState<T>(initialProduct);
  const [billingPeriod, setBillingPeriod] =
    useState<BillingPeriod>(initialBillingPeriod);

  return {
    selectedProduct,
    billingPeriod,
    setSelectedProduct,
    setBillingPeriod,
  };
}
